import React, { useState } from "react";
import { useApp } from "@/state/AppContext";
import { PaymentQr } from "@/components/PaymentQr";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fromMinor } from "@/lib/format";

/** Shared status/QR/claim/cancel block for a UPI payment — used both by the standalone "Pay this
 * shop" page (counter payments) and inline on an order/booking/appointment card (checkout
 * payments), so the website has one implementation instead of two drifting copies. */
export function PaymentPanel({ payment, requireUtr = false, onClaim, onCancel, busy = false, error = "", testId }) {
  const { t } = useApp();
  const [utr, setUtr] = useState("");

  if (!payment) return null;

  return (
    <div className="mt-4 space-y-3 rounded-2xl border border-border p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{t("payment.title")}</p>
        <Badge variant={payment.status === "received" ? "default" : "secondary"} data-testid={`${testId}-status`}>
          {t(`payment.status.${payment.status}`)}
        </Badge>
      </div>

      {payment.status === "pending" && (
        <div className="space-y-3">
          <PaymentQr
            upiId={payment.upi_id}
            payeeName={payment.payee_name}
            amount={fromMinor(payment.amount_minor_units)}
            note={payment.note}
            testId={`${testId}-qr`}
          />
          <p className="text-xs text-muted-foreground">{t("payment.manualNotice")}</p>
          <Input
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            placeholder={t(requireUtr ? "payment.utrRequired" : "payment.utrOptional")}
            data-testid={`${testId}-utr-input`}
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button
              size="sm"
              className="rounded-full"
              disabled={busy || (requireUtr && !utr.trim())}
              onClick={() => onClaim(utr.trim())}
              data-testid={`${testId}-claim-btn`}
            >
              {t("payment.ivePaid")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={busy}
              onClick={onCancel}
              data-testid={`${testId}-cancel-btn`}
            >
              {t("payment.cancel")}
            </Button>
          </div>
        </div>
      )}

      {payment.status === "awaiting_confirmation" && (
        <p className="text-xs text-muted-foreground">{t("payment.status.awaiting_confirmation")}</p>
      )}
    </div>
  );
}
